import { v4 as uuidv4 } from "uuid";

import { storage } from "../firebaseConfig";

export const COMPANY_LOGO_MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
export const COMPANY_LOGO_ACCEPTED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;

export type CompanyLogoValidationErrorCode = "invalid_type" | "too_large";

export class CompanyLogoValidationError extends Error {
  constructor(public readonly code: CompanyLogoValidationErrorCode) {
    super(code);
    this.name = "CompanyLogoValidationError";
  }
}

const extensionByContentType: Record<
  (typeof COMPANY_LOGO_ACCEPTED_TYPES)[number],
  string
> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

const validatePathSegment = (value: string) => {
  if (!value || value.includes("/")) {
    throw new Error("Invalid company logo path");
  }
};

export const getCompanyLogoStoragePrefix = (
  workspaceId: string,
  companyId: string,
) => {
  validatePathSegment(workspaceId);
  validatePathSegment(companyId);
  return `${workspaceId}/companies/${companyId}/branding/`;
};

export const validateCompanyLogo = (file: File) => {
  if (
    !COMPANY_LOGO_ACCEPTED_TYPES.includes(
      file.type as (typeof COMPANY_LOGO_ACCEPTED_TYPES)[number],
    )
  ) {
    throw new CompanyLogoValidationError("invalid_type");
  }
  if (file.size > COMPANY_LOGO_MAX_FILE_SIZE_BYTES) {
    throw new CompanyLogoValidationError("too_large");
  }
};

export interface UploadedCompanyLogo {
  logoUrl: string;
  logoStoragePath: string;
}

export const uploadCompanyLogo = async ({
  file,
  workspaceId,
  companyId,
  uploadedBy,
}: {
  file: File;
  workspaceId: string;
  companyId: string;
  uploadedBy: string;
}): Promise<UploadedCompanyLogo> => {
  validateCompanyLogo(file);
  const contentType = file.type as (typeof COMPANY_LOGO_ACCEPTED_TYPES)[number];
  const storagePath = `${getCompanyLogoStoragePrefix(workspaceId, companyId)}${uuidv4()}.${extensionByContentType[contentType]}`;
  const fileRef = storage.ref(storagePath);

  await fileRef.put(file, {
    contentType,
    cacheControl: "public,max-age=31536000,immutable",
    customMetadata: { workspaceId, companyId, uploadedBy },
  });

  return {
    logoUrl: await fileRef.getDownloadURL(),
    logoStoragePath: storagePath,
  };
};

export const deleteCompanyLogo = async ({
  storagePath,
  workspaceId,
  companyId,
}: {
  storagePath?: string | null;
  workspaceId: string;
  companyId: string;
}) => {
  if (!storagePath) return;
  const prefix = getCompanyLogoStoragePrefix(workspaceId, companyId);
  const fileName = storagePath.slice(prefix.length);
  if (!storagePath.startsWith(prefix) || !fileName || fileName.includes("/")) {
    throw new Error("Refusing to delete a logo outside the company directory");
  }

  try {
    await storage.ref(storagePath).delete();
  } catch (error) {
    if ((error as { code?: string }).code !== "storage/object-not-found") {
      throw error;
    }
  }
};
